<template>
    <div>
        <q-btn
            icon="map"
            :label="
                window.translations
                    ? window.translations['admin.map.draw_route']
                    : 'Route zeichnen'
            "
            @click="showDialog = true"
            color="primary"
            class="q-mb-md"
        />
        <q-dialog
            v-model="showDialog"
            maximized
            transition-show="slide-up"
            transition-hide="slide-down"
        >
            <q-card>
                <q-toolbar class="bg-primary text-white">
                    <q-btn flat round dense icon="close" v-close-popup />
                    <q-toolbar-title>{{
                        window.translations
                            ? window.translations["admin.map.draw_route"]
                            : "Route zeichnen"
                    }}</q-toolbar-title>
                </q-toolbar>

                <q-card-section
                    class="q-pa-none"
                    style="height: calc(100vh - 50px)"
                >
                    <div
                        id="map-drawer"
                        style="height: 100%; width: 100%"
                    ></div>
                </q-card-section>

                <q-btn
                    fab
                    icon="check"
                    color="secondary"
                    @click="confirmSelection"
                    :label="
                        window.translations
                            ? window.translations['admin.map.confirm']
                            : 'Übernehmen'
                    "
                    class="absolute-bottom-right q-ma-lg"
                    style="z-index: 3000"
                />
            </q-card>
        </q-dialog>

        <input type="hidden" :name="inputName" :value="polygonsJson" />

        <div
            v-if="polygonsJson && polygonsJson !== '[]'"
            class="text-caption q-mt-sm"
        >
            {{
                window.translations
                    ? window.translations["admin.map.polygons_drawn"]
                    : "Polygone gezeichnet."
            }}
        </div>
    </div>
</template>

<style scoped>
:deep(.monochrome-tiles) {
    filter: grayscale(100%);
}
</style>

<script setup>
import { ref, onMounted, nextTick, watch } from "vue";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";

// Fix missing icon issues in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: iconRetinaUrl,
    iconUrl: iconUrl,
    shadowUrl: shadowUrl,
});

const props = defineProps({
    initialPolygons: {
        type: String,
        default: "[]",
    },
    allLocations: {
        type: [String, Array],
        default: "[]",
    },
    inputName: {
        type: String,
        default: "polygons",
    },
    monochrome: {
        type: Boolean,
        default: false,
    },
    centerLat: {
        type: [Number, String],
        default: 51.165691,
    },
    centerLng: {
        type: [Number, String],
        default: 10.451526,
    },
});

const showDialog = ref(false);
const polygonsJson = ref(props.initialPolygons);
let map = null;
let drawnItems = null;
let locationsLayer = null;

const initMap = () => {
    if (map) return; // Already initialized

    map = L.map("map-drawer", { maxZoom: 20 }).setView(
        [parseFloat(props.centerLat), parseFloat(props.centerLng)],
        13,
    );
    L.tileLayer("/tiles/{z}/{x}/{y}.png", {
        attribution:
            '&copy; <a target="_blank" rel="noopener" href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        className: props.monochrome ? "monochrome-tiles" : "",
        maxZoom: 20,
        maxNativeZoom: 18,
    }).addTo(map);

    locationsLayer = L.layerGroup();

    let locationsData = [];
    try {
        locationsData =
            typeof props.allLocations === "string"
                ? JSON.parse(props.allLocations)
                : props.allLocations;
    } catch (e) {
        console.error("Failed to parse all locations");
    }

    locationsData.forEach((loc) => {
        let marker;
        if (loc.icon || loc.color) {
            const icon = L.divIcon({
                html: `<div class="custom-marker" style="color: ${loc.color || "primary"}; width: 32px; height: 32px; display: flex; justify-content: center; align-items: center;"><i class="q-icon notranslate material-icons" style="font-size: 32px;">${loc.icon || "place"}</i></div>`,
                iconAnchor: [16, 32],
                popupAnchor: [0, -32],
                className: "",
            });
            marker = L.marker([loc.latitude, loc.longitude], { icon: icon });
        } else {
            marker = L.marker([loc.latitude, loc.longitude]);
        }
        marker.bindPopup(loc.name);
        locationsLayer.addLayer(marker);
    });

    drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    let initialData = null;
    try {
        initialData = JSON.parse(props.initialPolygons);
    } catch (e) {
        console.error("Failed to parse initial polygons");
    }

    if (
        initialData &&
        (Array.isArray(initialData) ||
            (typeof initialData === "object" &&
                Object.keys(initialData).length > 0))
    ) {
        // If it's valid GeoJSON
        try {
            L.geoJSON(initialData, {
                onEachFeature: function (feature, layer) {
                    drawnItems.addLayer(layer);
                },
            });

            if (drawnItems.getLayers().length > 0) {
                map.fitBounds(drawnItems.getBounds());
            }
        } catch (e) {
            console.error("Failed to add initial polygons to map", e);
        }
    }

    const drawControl = new L.Control.Draw({
        edit: {
            featureGroup: drawnItems,
        },
        draw: {
            polygon: true,
            polyline: true,
            rectangle: false,
            circle: false,
            marker: false,
            circlemarker: false,
        },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, function (event) {
        const layer = event.layer;
        drawnItems.addLayer(layer);
        updatePolygons();
    });

    map.on(L.Draw.Event.EDITED, function (event) {
        updatePolygons();
    });

    map.on(L.Draw.Event.DELETED, function (event) {
        updatePolygons();
    });

    // Custom Control for Toggling Locations
    const LocationToggleControl = L.Control.extend({
        options: {
            position: "topright",
        },
        onAdd: function (map) {
            const container = L.DomUtil.create(
                "div",
                "leaflet-bar leaflet-control leaflet-control-custom",
            );
            container.style.backgroundColor = "white";
            container.style.width = "34px";
            container.style.height = "34px";
            container.style.display = "flex";
            container.style.justifyContent = "center";
            container.style.alignItems = "center";
            container.style.cursor = "pointer";
            container.title = window.translations
                ? window.translations["admin.map.toggle_locations"]
                : "Standorte anzeigen/ausblenden";

            const icon = L.DomUtil.create("i", "material-icons", container);
            icon.innerHTML = "place";
            icon.style.fontSize = "20px";

            container.onclick = function () {
                if (map.hasLayer(locationsLayer)) {
                    map.removeLayer(locationsLayer);
                    container.style.color = "black";
                } else {
                    map.addLayer(locationsLayer);
                    container.style.color = "#1976D2";
                }
            };

            return container;
        },
    });
    map.addControl(new LocationToggleControl());
};

const updatePolygons = () => {
    if (drawnItems) {
        const data = drawnItems.toGeoJSON();
        polygonsJson.value = JSON.stringify(data);
    }
};

watch(showDialog, async (newVal) => {
    if (newVal) {
        await nextTick();
        setTimeout(() => {
            initMap();
            if (map) {
                map.invalidateSize();
            }
        }, 100); // Small timeout to ensure DOM is ready and sized
    }
});

const confirmSelection = () => {
    updatePolygons();
    showDialog.value = false;
};
</script>
